import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { ColumnEnlargeIcon } from '@pleio/icons/column-enlarge'
import { ColumnShrinkIcon } from '@pleio/icons/column-shrink'
import { DeleteIcon } from '@pleio/icons/delete'

import ActionsBarButton from 'js/page/PageEditor/components/ActionsBarButton'
import {
    getDefaultColumnWidth,
    getLargeColumnWidth,
    getSmallColumnWidth,
} from 'js/page/PageEditor/helpers/getColumnWidth'
import PageEditorContext from 'js/page/PageEditor/PageEditorContext'
import ThemeProvider from 'js/theme/ThemeProvider'

import ActionsBar, { ActionsBarProps } from './ActionsBar'

type ColumnActionsProps = Omit<ActionsBarProps, 'children'> & {
    width: number[]
    rowIndex: number
    columnIndex: number
    numberOfColumns: number
}

const ColumnActions = ({
    width,
    rowIndex,
    columnIndex,
    numberOfColumns,
    show,
    hide,
    onFocus,
    onBlur,
}: ColumnActionsProps) => {
    const { t } = useTranslation()

    const { setRows } = useContext(PageEditorContext)

    if (numberOfColumns === 1) {
        return null
    }

    const handleRemove = () => {
        const newWidth = getDefaultColumnWidth(numberOfColumns - 1)

        setRows((prev: any) =>
            prev.map((row: any, idx: number) =>
                idx === rowIndex
                    ? {
                          ...row,
                          columns: row.columns
                              .filter(
                                  (_: any, cIdx: number) =>
                                      cIdx !== columnIndex,
                              )
                              .map((col: any) => ({
                                  ...col,
                                  width: [newWidth],
                              })),
                      }
                    : row,
            ),
        )
    }

    const handleResize = () => {
        const newWidth = widthIsLarge ? DEFAULT_WIDTH : LARGE_WIDTH
        const newOtherWidth = widthIsLarge ? DEFAULT_WIDTH : SMALL_WIDTH

        setRows((prev: any) =>
            prev.map((row: any, idx: number) =>
                idx === rowIndex
                    ? {
                          ...row,
                          columns: row.columns.map(
                              (col: any, cIdx: number) => ({
                                  ...col,
                                  width:
                                      cIdx === columnIndex
                                          ? [newWidth]
                                          : [newOtherWidth],
                              }),
                          ),
                      }
                    : row,
            ),
        )
    }

    const DEFAULT_WIDTH = getDefaultColumnWidth(numberOfColumns)
    const SMALL_WIDTH = getSmallColumnWidth(numberOfColumns)
    const LARGE_WIDTH = getLargeColumnWidth(numberOfColumns)

    const widthIsLarge = width[0] === LARGE_WIDTH
    const resizeIsEnabled = [2, 3].includes(numberOfColumns)

    const resizeLabel = widthIsLarge
        ? t('page.shrink-column')
        : t('page.enlarge-column')

    return (
        <ThemeProvider theme="pleio">
            <ActionsBar
                show={show}
                hide={hide}
                isColumn={true}
                onFocus={onFocus}
                onBlur={onBlur}
            >
                {resizeIsEnabled && (
                    <ActionsBarButton
                        onClick={handleResize}
                        tooltip={resizeLabel}
                    >
                        {widthIsLarge ? (
                            <ColumnShrinkIcon />
                        ) : (
                            <ColumnEnlargeIcon />
                        )}
                    </ActionsBarButton>
                )}
                <ActionsBarButton
                    onClick={handleRemove}
                    tooltip={t('page.delete-column')}
                >
                    <DeleteIcon />
                </ActionsBarButton>
            </ActionsBar>
        </ThemeProvider>
    )
}

export default ColumnActions
